import { useAppKit, useAppKitAccount } from '@reown/appkit/react'
import { useDisconnect } from 'wagmi'
import {
	isWalletConnected,
	isWalletDisconnected,
	isWalletLoading,
} from '@/constants/wallet-status'
import { useAuthStore } from '@/stores/authStore'
import type { AuthUser } from '@/stores/types'
import { useSiweAuth } from './useSiweAuth'

export function useWalletConnection() {
	const { open } = useAppKit()
	const { address: walletAddress, status, embeddedWalletInfo } = useAppKitAccount()
	const { disconnect } = useDisconnect()
	const updateUser = useAuthStore((state) => state.auth.updateUser)
	const { authenticate, getAuthMethod } = useSiweAuth()

	const reownSocialLoginUserInfo = {
		email: embeddedWalletInfo?.user?.email,
		username: embeddedWalletInfo?.user?.username,
	}

	const isLoading = status ? isWalletLoading(status) : false
	const isConnected = status ? isWalletConnected(status) : false
	const isDisconnected = status ? isWalletDisconnected(status) : false

	const handleConnect = () => open()

	const updateUserInfo = () => {
		const userUpdate: Partial<AuthUser> = {
			walletAddress: walletAddress || '',
			...reownSocialLoginUserInfo,
		}
		updateUser(userUpdate)
	}

	const handleConnectOnRamp = () =>
		open({
			view: 'OnRampProviders',
		})

	const handleDisconnect = () => {
		disconnect()
	}

	const handleFullLogout = () => {
		// Disconnect wallet first
		disconnect()

		// Clear all localStorage items (including Reown/Wagmi data)
		const keysToRemove = []
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i)
			if (key) {
				keysToRemove.push(key)
			}
		}
		keysToRemove.forEach((key) => localStorage.removeItem(key))

		// Clear sessionStorage
		sessionStorage.clear()

		// Clear all cookies more thoroughly
		const cookies = document.cookie.split(';')
		cookies.forEach((cookie) => {
			const eqPos = cookie.indexOf('=')
			const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim()
			if (name) {
				// Clear for current domain
				document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
				document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`
				document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${window.location.hostname}`
				// Also try with secure flag
				document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;secure`
				document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname};secure`
			}
		})

		// Clear auth store
		useAuthStore.getState().auth.clearAuth()

		// Force clear any remaining Reown/Wagmi state
		if (typeof window !== 'undefined' && window.ethereum) {
			// Clear any Reown-specific data
			try {
				delete (window as any).ethereum
			} catch (e) {
				console.log('Could not clear ethereum object:', e)
			}
		}

		// Reload the page to ensure clean state
		window.location.reload()
	}

	/**
	 * Handle SIWE/SIWX authentication after wallet connection
	 * This should be called manually, not automatically
	 */
	const handleSiweAuth = async () => {
		if (!isConnected) {
			console.warn('Wallet not connected')
			return
		}

		try {
			const result = await authenticate()
			if (result.success) {
				// Update user info with roles
				updateUserInfo()
			} else {
				console.error('SIWE/SIWX authentication failed:', result.error)
			}
		} catch (error) {
			console.error('SIWE/SIWX authentication error:', error)
		}
	}

	return {
		status,
		walletAddress,
		isLoading,
		isConnected,
		isDisconnected,
		handleConnect,
		handleConnectOnRamp,
		handleDisconnect,
		handleFullLogout,
		updateUserInfo,
		handleSiweAuth,
		embeddedWalletInfo,
		getAuthMethod,
	}
}
